import { useState } from 'react';
import { Modal } from './Modal.js';
import { SORT_FIELDS, sortFieldFor, type SortDir, type SortPref } from '../lib/contactSort.js';

/**
 * ehr#339: the Contacts Sort window, laid out as Chase's CRM sort-modal mockup (Sort by,
 * Direction, the empty-values note, Reset to default / Cancel / Apply) in the EHR's own
 * modal, which freezes the page behind it. Nothing changes until Apply; Reset to default
 * goes back to the last-name order at once.
 */
export function ContactSortModal({
  value,
  onApply,
  onReset,
  onClose,
}: {
  value: SortPref;
  onApply: (pref: SortPref) => void;
  onReset: () => void;
  onClose: () => void;
}) {
  // A column the window doesn't offer (Status, Email…) opens on Last name.
  const start = sortFieldFor(value.key) ?? SORT_FIELDS[1]!;
  const [key, setKey] = useState(start.key);
  const [dir, setDir] = useState<SortDir>(sortFieldFor(value.key) ? value.dir : 'asc');
  const field = SORT_FIELDS.find((f) => f.key === key) ?? start;

  return (
    <Modal
      title="Sort contacts"
      onClose={onClose}
      className="csm-modal"
      actions={
        <>
          <button type="button" className="csm-reset" onClick={onReset}>
            Reset to default
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn primary" onClick={() => onApply({ key, dir })}>
            Apply
          </button>
        </>
      }
    >
      <p className="csm-sub">Your choice is saved until you change it.</p>
      <fieldset className="csm-group">
        <legend>Sort by</legend>
        {SORT_FIELDS.map((f) => (
          <label key={f.key} className="csm-option">
            <input
              type="radio"
              name="csm-field"
              value={f.key}
              checked={key === f.key}
              onChange={() => setKey(f.key)}
            />
            <span>{f.label}</span>
          </label>
        ))}
      </fieldset>
      <fieldset className="csm-group">
        <legend>Direction</legend>
        <div className="csm-dir">
          {(['asc', 'desc'] as const).map((d) => (
            <label key={d} className="csm-option">
              <input
                type="radio"
                name="csm-dir"
                value={d}
                checked={dir === d}
                onChange={() => setDir(d)}
              />
              <span>{d === 'asc' ? field.asc : field.desc}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <p className="csm-note">Contacts with an empty value always appear at the bottom.</p>
    </Modal>
  );
}
